import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import {
  CollateralBasketProductsPanelGridRow,
  CollateralBasketSelectedProductsGrid,
  CollateralBasketProductsGrid
} from 'view/shared/collateralBasket'
import { productSectorRecordSelector, productRecordSelector } from 'selectors/productSelectors'
import { ProductSector, Product, Collateral, CollateralBasketType, EntityType, ValidationIssue } from 'api'
import { CollateralBasketPanelProps } from './CollateralBasketDialog'
import { DialogRow } from 'view/components/inputs'
import { createCollateralByCurrencySelector } from 'selectors/collateralSelectors'
import { Locale, Translation } from 'locale'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { HorizontalLayout } from 'view/styles/constants'
import { ValidationIssuesContainer } from 'view/shared/error'
import { isActiveLike } from 'model'
import { TextButton, TextButtonType } from 'view/components/buttons'

interface CollateralBasketProductsPanelProps extends CollateralBasketPanelProps {
  validationIssues: ValidationIssue[]
  productSectors: Record<string, ProductSector>
  products: Record<string, Product>
  collateralsByCurrency: Collateral[]
  getCollateral: (collateralId: string) => Collateral | null
  viewOnly: boolean
}

const CollateralBasketCurrencySelector = (_: {}, props: CollateralBasketPanelProps) => props.collateralBasketProductSector?.basketCurrency

const mapStateToProps = createStructuredSelector({
  productSectors: productSectorRecordSelector,
  products: productRecordSelector,
  collateralsByCurrency: createCollateralByCurrencySelector(CollateralBasketCurrencySelector),
  getCollateral: createEntityGetterSelector(EntityType.COLLATERAL)
})

const Wrapper = styled.div`
  height: 430px;
  width: 500px;
`

const CenterText = styled.div`
  text-align: center;
  margin-top: 200px;
`

const SelectedGridWrapper = styled.div`
  height: 405px;
  width: 540px;
`

const SelectedTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  place-content: space-between;
`

export const CollateralBasketProductsPanelComponent: React.FunctionComponent<CollateralBasketProductsPanelProps> = props => {
  const {
    productSectors,
    products,
    collateralBasket,
    collateralBasketProductSector,
    collateralsByCurrency,
    getCollateral,
    validationIssues,
    onUpdate,
    viewOnly
  } = props

  const collateralBasketMarket = collateralBasketProductSector ? products[collateralBasketProductSector.productId]?.market : undefined
  const collateralBasketMicCode = collateralBasketProductSector ? products[collateralBasketProductSector.productId]?.micCode : undefined
  const collateralsByCurrencyAndMic = collateralsByCurrency.filter(
    collateral => products[productSectors[collateral.productSectorId]?.productId]?.micCode === collateralBasketMicCode
  )

  const addCollateralId = (collateralIds: string[]) => {
    const newCollateralIds = collateralBasket.collateralIds
      ? [...new Set(collateralBasket.collateralIds.concat(collateralIds))]
      : collateralIds
    onUpdate({ collateralIds: newCollateralIds })
  }

  const removeCollateralId = (collateralIds: string[]) => {
    const newCollateralIds = collateralBasket.collateralIds.filter(isin => {
      return !collateralIds.includes(isin)
    })
    onUpdate({ collateralIds: newCollateralIds })
  }

  const removeAllCollateralId = () => {
    onUpdate({ collateralIds: [] })
  }

  const productsByCollateralRowData = collateralsByCurrencyAndMic
    .filter(collateral => isActiveLike(collateral))
    .map(collateral => {
      return {
        rowId: collateral.id,
        collateral,
        collaterals: null,
        productSector: productSectors?.[collateral?.productSectorId],
        product: products?.[productSectors?.[collateral?.productSectorId]?.productId],
        selected: collateralBasket.collateralIds.includes(collateral.id)
      }
    })

  const productsBySectorRowData = Object.keys(productSectors)
    .map(productSectorId => {
      const collateralsPerSector = collateralsByCurrencyAndMic.filter(
        collateral => collateral.productSectorId === productSectorId && isActiveLike(collateral)
      )
      return {
        rowId: productSectorId,
        collateral: null,
        collaterals: collateralsPerSector,
        productSector: productSectors?.[productSectorId],
        product: products?.[productSectors?.[productSectorId]?.productId],
        selected: collateralsPerSector.every(collateral => collateralBasket.collateralIds.includes(collateral.id))
      }
    })
    .filter((row: CollateralBasketProductsPanelGridRow) => row.collaterals?.length !== 0)

  const selectedCollateralsRowData = collateralBasket.collateralIds.map(collateralId => {
    const collateral = getCollateral(collateralId)
    return {
      rowId: collateralId,
      collateral,
      collaterals: null,
      productSector: collateral ? productSectors?.[collateral?.productSectorId] : null,
      product: collateral ? products?.[productSectors?.[collateral?.productSectorId]?.productId] : null,
      selected: true
    }
  })

  const productsByCollateralPanel = (
    <DialogRow horizontalLayout={HorizontalLayout.SpaceBetween}>
      <Wrapper>
        <CollateralBasketProductsGrid
          rowData={productsByCollateralRowData}
          removeCollateralId={removeCollateralId}
          addCollateralId={addCollateralId}
          collateralBasketMarket={collateralBasketMarket}
          showCollaterals={true}
          viewOnly={viewOnly}
        />
      </Wrapper>
      {collateralBasket.collateralIds && collateralBasket.collateralIds.length > 0 && (
        <SelectedGridWrapper>
          <SelectedTitleWrapper>
            <div>
              {Locale.translate(Translation.TC_SELECTED_PRODUCTS)} ({collateralBasket.collateralIds.length})
              {validationIssues.length > 0 && (
                <ValidationIssuesContainer>
                  {validationIssues.map((validationIssue, index) => (
                    <li key={index}>{validationIssue.errorMessage}</li>
                  ))}
                </ValidationIssuesContainer>
              )}
            </div>
            <TextButton type={TextButtonType.Primary} disabled={false} onClick={removeAllCollateralId}>
              {Locale.translate(Translation.TC_REMOVE_ALL)}
            </TextButton>
          </SelectedTitleWrapper>
          <CollateralBasketSelectedProductsGrid
            rowData={selectedCollateralsRowData}
            canDelete={!viewOnly}
            removeCollateralId={removeCollateralId}
            collateralBasketMarket={collateralBasketMarket}
          />
        </SelectedGridWrapper>
      )}
    </DialogRow>
  )

  const productsBySectorPanel = (
    <DialogRow>
      <Wrapper>
        <CollateralBasketProductsGrid
          rowData={productsBySectorRowData}
          removeCollateralId={removeCollateralId}
          addCollateralId={addCollateralId}
          showCollaterals={false}
          viewOnly={viewOnly}
        />
      </Wrapper>
      {collateralBasket.collateralIds && collateralBasket.collateralIds.length > 0 && (
        <SelectedGridWrapper>
          {Locale.translate(Translation.TC_MATCHING_RESULTS)} ({collateralBasket.collateralIds.length})
          {validationIssues.length > 0 && (
            <ValidationIssuesContainer>
              {validationIssues.map((validationIssue, index) => (
                <li key={index}>{validationIssue.errorMessage}</li>
              ))}
            </ValidationIssuesContainer>
          )}
          <CollateralBasketSelectedProductsGrid rowData={selectedCollateralsRowData} canDelete={false} />
        </SelectedGridWrapper>
      )}
    </DialogRow>
  )

  const getProductsPanelBasketType = (collateralBasketType: CollateralBasketType | null | undefined, isDynamicBasket: boolean | null) => {
    switch (collateralBasketType) {
      case CollateralBasketType.GC_BESPOKE:
        return isDynamicBasket ? productsBySectorPanel : productsByCollateralPanel
      case CollateralBasketType.EUROCLEAR:
        return <CenterText>{Locale.translate(Translation.SC_NO_COLLATERAL_EUROCLEAR)}</CenterText>
      case CollateralBasketType.DBV:
        return <CenterText>{Locale.translate(Translation.SC_NO_COLLATERAL_DBV)}</CenterText>
      case CollateralBasketType.BONY:
        return <CenterText>{Locale.translate(Translation.SC_NO_COLLATERAL_BONY)}</CenterText>
      case CollateralBasketType.GC_PLUS:
        return <CenterText>{Locale.translate(Translation.SC_NO_COLLATERAL_GC_PLUS)}</CenterText>
      case CollateralBasketType.COLLATERAL:
        return <CenterText>{Locale.translate(Translation.SC_NO_COLLATERAL_COLLATERAL)}</CenterText>
      case CollateralBasketType.CASH:
        return <CenterText>{Locale.translate(Translation.SC_NO_COLLATERAL_CASH)}</CenterText>
      case CollateralBasketType.CLEAR_STREAM:
        return <CenterText>{Locale.translate(Translation.SC_NO_COLLATERAL_CLEAR_STREAM)}</CenterText>
      default:
        return null
    }
  }

  return <>{getProductsPanelBasketType(collateralBasketProductSector?.basketType, collateralBasket.isDynamicBasket)}</>
}

export const CollateralBasketProductsPanel = connect(mapStateToProps)(CollateralBasketProductsPanelComponent)
